import React, { useRef, useState } from "react";
import { Form, FormInstance, Table } from "antd";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { useOpenGymReservationMutations } from "../../../api/mutations";
import dayjs from "dayjs";
import { useGetAllOpenGymReservationsQuery } from "../../../api/queries";
import { openSuccessMessage } from "../../../store/message/messageSlice";
import { handleError } from "../../../helper/errorHandler";
import { ColumnsType } from "antd/es/table";
import cancelIcon from "../../../assets/images/icons/decline.svg";
import { IOpenGymReservation } from "../../../models/OpenGymReservation";
import MainTitle from "../../../components/ui/MainTitle/MainTitle";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import FormItemDatePicker from "../../../components/ui/FormItemDatePicker/FormItemDatePicker";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import searchIconWhite from "../../../assets/images/icons/search-icon-white.svg";

const OpenGyms = () => {
  const formRef = useRef<FormInstance>(null);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { cancel } = useOpenGymReservationMutations();

  const [filters, setFilters] = useState<{ from: Date; to: Date; searchValue: null | string }>({
    from: dayjs().subtract(7, "days").toDate(),
    to: dayjs().toDate(),
    searchValue: null,
  });
  const {
    data: openGymReservations = [],
    isLoading,
    refetch,
  } = useGetAllOpenGymReservationsQuery({ ...filters, userId: null });

  const onFinish = (values: { from: Date; to: Date; searchValue: null | string }) => {
    setFilters({
      from: dayjs(values.from).toDate(),
      to: dayjs(values.to).toDate(),
      searchValue: values.searchValue ? values.searchValue : null,
    });
  };

  const cancelReservation = (reservation: IOpenGymReservation) => {
    cancel(
      { openGymReservationId: reservation.id, gymListingId: reservation.gymListingId },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("ReservationCanceledSuccessfully")));
          refetch();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  const columns: ColumnsType<IOpenGymReservation> = [
    {
      title: t("MemberName"),
      render: (data: IOpenGymReservation) => {
        return data.userFullName;
      },
    },
    {
      title: t("BookingDate"),
      render: (data: IOpenGymReservation) => {
        return dayjs(data.createdAt).format("DD/MM/YYYY");
      },
    },
    {
      title: t("TimeSlot"),
      render: (data: IOpenGymReservation) => {
        return `${dayjs(data.startDate).format("DD/MM/YYYY hh:mm A")} ${dayjs(data.endDate).format("hh:mm A")}`;
      },
    },
    {
      title: t("GymName"),
      render: (data: IOpenGymReservation) => {
        return data.gymName;
      },
    },
    {
      title: t("Action"),
      render: (data: IOpenGymReservation) => {
        return (
          <>
            {data.canBeCancelled ? (
              <div>
                <button
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "6px",
                    fontFamily: "Open Sans",
                    fontSize: "16px",
                  }}
                  onClick={() => cancelReservation(data)}
                >
                  <img src={cancelIcon} alt={"cancel"} />
                  {t("Cancel")}
                </button>
              </div>
            ) : (
              "-"
            )}
          </>
        );
      },
    },
  ];

  return (
    <div className={"gyms-page"}>
      <MainTitle text={t("OpenGymReservations")} />

      <div className={"top-row"}>
        <Form
          className={"form"}
          ref={formRef}
          name="group-classes-filter"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
          initialValues={{
            from: dayjs(filters.from),
            to: dayjs(filters.to),
          }}
        >
          <FormItemInput
            name={"searchValue"}
            label={" "}
            rules={{ required: false }}
            placeholder={t("SearchByGymName")}
            style={{
              width: "30%",
            }}
          />
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"from"}
            label={t("From")}
            rules={{ required: true }}
            style={{
              width: "30%",
            }}
          />
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"to"}
            label={t("To")}
            rules={{ required: true }}
            style={{
              width: "30%",
            }}
          />
          <div className={"btn-wrapper"}>
            <CustomButton
              text={""}
              type={"submit"}
              typeStyle={"fill"}
              icon={searchIconWhite}
              altIcon={"search"}
            />
          </div>
        </Form>
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={openGymReservations}
        pagination={{ total: openGymReservations.length }}
        loading={isLoading}
      />
    </div>
  );
};

export default OpenGyms;
